import { useState } from "react";
import { useNavigate } from "react-router";
import type { MatrixListItemUi } from "@/domains/matrix";
import { buildCopyName } from "../lib/copy-name";
import { useDuplicateMatrix } from "./use-duplicate-matrix";

type MatrixTarget = { collectionId: string; name: string };

// Render `MatrixRowActionDialogs` beside the list: the menus unmount as they open.
export function useMatrixRowActions(matrices: MatrixListItemUi[]) {
  const navigate = useNavigate();
  const duplicateMatrix = useDuplicateMatrix();
  const [pendingRename, setPendingRename] = useState<MatrixTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MatrixTarget | null>(null);

  function duplicate(matrix: MatrixTarget) {
    duplicateMatrix.mutate(
      {
        collectionId: matrix.collectionId,
        name: buildCopyName(
          matrix.name,
          matrices.map((entry) => entry.name),
        ),
      },
      {
        onSuccess: ({ tenant_collection_id }) =>
          void navigate(`/matrix/${tenant_collection_id}`),
      },
    );
  }

  function isDuplicating(collectionId: string) {
    return (
      duplicateMatrix.isPending &&
      duplicateMatrix.variables?.collectionId === collectionId
    );
  }

  return {
    duplicate,
    isDuplicating,
    duplicateError: duplicateMatrix.error,
    // Spread onto a MatrixActionsMenu for one row.
    menuPropsFor: (matrix: MatrixTarget) => ({
      name: matrix.name,
      onRename: () => setPendingRename(matrix),
      onDuplicate: () => duplicate(matrix),
      onRequestDelete: () => setPendingDelete(matrix),
      isDuplicating: isDuplicating(matrix.collectionId),
    }),
    // Spread onto MatrixRowActionDialogs.
    dialogProps: {
      pendingRename,
      pendingDelete,
      duplicateError: duplicateMatrix.error,
      onCloseRename: () => setPendingRename(null),
      onCloseDelete: () => setPendingDelete(null),
      onDismissDuplicateError: () => duplicateMatrix.reset(),
    },
  };
}
